import React from 'react';
import { PiImageLight } from 'react-icons/pi';
import { Text } from 'src/modules/shared/components/atoms/text';
import type { WebsiteLogoSize } from '../../utils/website-logo-guidance.util';

/** Props for the logo preview tile. */
interface WebsiteLogoPreviewProps {
  /** URL of the current logo, when there is one. */
  logoUrl?: string;
  /** Accessible description of the logo image. */
  alt: string;
  /** Text shown in the tile while there is no logo. */
  emptyText: string;
  /** Called with the image's natural size once it loads. */
  onMeasure: (size: WebsiteLogoSize) => void;
}

/**
 * White tile that shows the logo about as large as the site headers do, or an
 * empty state before one is uploaded.
 *
 * @param props - Logo, texts and the measure callback.
 * @returns The preview tile.
 */
export const WebsiteLogoPreview: React.FC<WebsiteLogoPreviewProps> = ({
  logoUrl,
  alt,
  emptyText,
  onMeasure,
}) => (
  <div className="flex h-20 w-40 shrink-0 items-center justify-center rounded-lg border border-neutral-200 bg-white p-3">
    {logoUrl ? (
      <img
        src={logoUrl}
        alt={alt}
        className="max-h-full max-w-full object-contain"
        onLoad={(event) =>
          onMeasure({
            width: event.currentTarget.naturalWidth,
            height: event.currentTarget.naturalHeight,
          })
        }
      />
    ) : (
      <div className="flex flex-col items-center gap-1 text-neutral-400">
        <PiImageLight className="h-6 w-6" aria-hidden />
        <Text size="tiny" color="secondary">
          {emptyText}
        </Text>
      </div>
    )}
  </div>
);
